import { request } from "@frontend/lib/http";

import type { JobValues } from "../schemas/job";
import type {
  DuplicateJobInput,
  Employee,
  Job,
  JobRow,
  ProjectContext,
} from "../types";
import { toJobInput } from "../utils/payload";

type RequestOptions = {
  signal?: AbortSignal;
};

export type JobApi = {
  fetchJobs(
    projectId: number,
    past?: boolean,
    options?: RequestOptions,
  ): Promise<JobRow[]>;
  fetchJob(
    projectId: number,
    jobId: number,
    options?: RequestOptions,
  ): Promise<Job>;
  createJob(
    projectId: number,
    input: JobValues,
    options?: RequestOptions,
  ): Promise<Job>;
  updateJob(
    projectId: number,
    jobId: number,
    input: JobValues,
    options?: RequestOptions,
  ): Promise<Job>;
  deleteJob(
    projectId: number,
    jobId: number,
    options?: RequestOptions,
  ): Promise<void>;
  duplicateJob(
    projectId: number,
    jobId: number,
    input: DuplicateJobInput,
    options?: RequestOptions,
  ): Promise<Job>;
  fetchProject(
    projectId: number,
    options?: RequestOptions,
  ): Promise<ProjectContext>;
  fetchEmployees(
    archived?: boolean,
    options?: RequestOptions,
  ): Promise<Employee[]>;
};

export const jobApi: JobApi = {
  fetchJobs(projectId, past = true, options) {
    return request({
      route: "api_project_jobs_list",
      params: { projectId },
      query: { past },
      ...options,
    });
  },

  fetchJob(projectId, jobId, options) {
    return request({
      route: "api_project_jobs_show",
      params: { projectId, jobId },
      ...options,
    });
  },

  createJob(projectId, input, options) {
    return request({
      route: "api_project_jobs_create",
      params: { projectId },
      method: "POST",
      body: { jobInput: toJobInput(input) },
      ...options,
    });
  },

  updateJob(projectId, jobId, input, options) {
    return request({
      route: "api_project_jobs_update",
      params: { projectId, jobId },
      method: "PUT",
      body: { jobInput: toJobInput(input) },
      ...options,
    });
  },

  deleteJob(projectId, jobId, options) {
    return request({
      route: "api_project_jobs_delete",
      params: { projectId, jobId },
      method: "DELETE",
      ...options,
    });
  },

  duplicateJob(projectId, jobId, input, options) {
    return request({
      route: "api_project_jobs_duplicate",
      params: { projectId, jobId },
      method: "POST",
      body: { duplicateJobInput: input },
      ...options,
    });
  },

  fetchProject(projectId, options) {
    return request({
      route: "api_projects_show",
      params: { projectId },
      ...options,
    });
  },

  fetchEmployees(archived = false, options) {
    return request({
      route: "api_employees_list",
      query: { archived },
      ...options,
    });
  },
};
